import { useMemo, useState } from "react";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  createNewsCommentAction,
  type NewsCommentSortMode,
  newsCommentsQueryOptions,
} from "@/lib/client/news-comment";
import { newsReactionsQueryOptions, toggleNewsReactionAction } from "@/lib/client/news-reaction";
import type { CurrentUser } from "@/lib/client/user";
import {
  buildCommentThreads,
  MAX_COMMENT_LENGTH,
  mergeReactionSummary,
} from "./news-comment-section-helpers";
import { NewsCommentSectionRenderer } from "./news-comment-section-renderer";
import { type NewsReactionEmoji } from "@/lib/utils/news-reaction";

interface NewsCommentSectionProps {
  articleId: string;
  currentUser: CurrentUser | null;
}

interface ReactionSubmitArgs {
  targetType: "news" | "comment";
  targetId: string;
  emoji: NewsReactionEmoji;
}

export function NewsCommentSection({ articleId, currentUser }: NewsCommentSectionProps) {
  const queryClient = useQueryClient();
  const [sortMode, setSortMode] = useState<NewsCommentSortMode>("recommended");
  const { data: comments = [] } = useSuspenseQuery(newsCommentsQueryOptions(articleId, sortMode));
  const rootComments = useMemo(() => comments.filter((comment) => !comment.parentId), [comments]);
  const reactionTargets = useMemo(
    () => rootComments.map((comment) => ({ targetType: "comment" as const, targetId: comment.id })),
    [rootComments],
  );
  const { data: reactions = [] } = useSuspenseQuery(newsReactionsQueryOptions(reactionTargets));
  const reactionMap = useMemo(() => mergeReactionSummary(reactions), [reactions]);
  const [newCommentContent, setNewCommentContent] = useState("");
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({});
  const [expandedReplies, setExpandedReplies] = useState<Record<string, boolean>>({});

  const { threads, authorNameById } = useMemo(() => buildCommentThreads(comments), [comments]);
  const mutation = useMutation({
    mutationFn: (variables: { parentId?: string | null; content: string }) =>
      createNewsCommentAction({
        articleId,
        content: variables.content,
        parentId: variables.parentId ?? null,
      }),
    onSuccess: () => {
      toast.success("评论发送成功");
      setNewCommentContent("");
      setReplyDrafts({});
      setReplyTo(null);
      queryClient.invalidateQueries({
        queryKey: ["news-comments", articleId],
      });
      queryClient.invalidateQueries({
        queryKey: ["news-reactions"],
      });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "评论发送失败，请稍后重试");
    },
  });

  const submitRoot = () => {
    const content = newCommentContent.trim();
    if (!content) {
      toast.error("请输入评论内容");
      return;
    }
    if (content.length > MAX_COMMENT_LENGTH) {
      toast.error(`评论内容不能超过${MAX_COMMENT_LENGTH}字`);
      return;
    }
    mutation.mutate({ content });
  };

  const submitReply = (parentId: string) => {
    const content = (replyDrafts[parentId] ?? "").trim();
    if (!content) {
      toast.error("请输入回复内容");
      return;
    }
    if (content.length > MAX_COMMENT_LENGTH) {
      toast.error(`回复内容不能超过${MAX_COMMENT_LENGTH}字`);
      return;
    }

    mutation.mutate({
      parentId,
      content,
    });
  };

  const toggleExpanded = (rootId: string) => {
    setExpandedReplies((prev) => ({
      ...prev,
      [rootId]: !prev[rootId],
    }));
  };

  const setReplyDraft = (id: string, content: string) => {
    setReplyDrafts((prev) => ({
      ...prev,
      [id]: content,
    }));
  };

  const reactionMutation = useMutation({
    mutationFn: ({ targetType, targetId, emoji }: ReactionSubmitArgs) =>
      toggleNewsReactionAction({
        targetType,
        targetId,
        emoji,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["news-reactions"],
      });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "reaction 失败，请稍后重试");
    },
  });

  const submitReaction = ({ targetType, targetId, emoji }: ReactionSubmitArgs) => {
    if (!currentUser) {
      toast.error("请先登录后再进行 reaction");
      return;
    }

    reactionMutation.mutate({
      targetType,
      targetId,
      emoji,
    });
  };

  return (
    <NewsCommentSectionRenderer
      currentUser={currentUser}
      commentsCount={comments.length}
      sortMode={sortMode}
      onSortModeChange={setSortMode}
      newCommentContent={newCommentContent}
      onNewCommentChange={setNewCommentContent}
      onSubmitRoot={submitRoot}
      isSubmittingComment={mutation.isPending}
      threads={threads}
      authorNameById={authorNameById}
      expandedReplies={expandedReplies}
      toggleExpanded={toggleExpanded}
      replyTo={replyTo}
      setReplyTo={setReplyTo}
      replyDrafts={replyDrafts}
      setReplyDraft={setReplyDraft}
      onSubmitReply={submitReply}
      reactionMap={reactionMap}
      onReactionSubmit={submitReaction}
      isReactionPending={reactionMutation.isPending}
    />
  );
}
